import React from "react";
import {
  StepperComponent,
  StepsDirective,
  StepDirective,
} from "@syncfusion/ej2-react-navigations";
import {
  ProgressBarComponent,
  ProgressBarAnnotationsDirective,
  ProgressBarAnnotationDirective,
  Inject,
  ProgressAnnotation,
} from "@syncfusion/ej2-react-progressbar";
import { MultiStep, MultiStepItemProps } from "./MultiStepContainer";
import { useActiveStep } from "./hooks/useActiveStep";
import { UCLColors, UCLColorKey } from "../colors/uclColors";

export interface MultiStepProgressBarProps<
  T extends MultiStepItemProps = MultiStepItemProps
> {
  steps: MultiStep<T>[];
  currentStepId: string;
  color: UCLColorKey;
  className?: string;
  style?: React.CSSProperties;
  onStepClick?: (stepId: string, substepId?: string) => void;
}

export const MultiStepProgressBar = <
  T extends MultiStepItemProps = MultiStepItemProps
>({
  steps,
  currentStepId,
  color,
  className,
  style,
  onStepClick,
}: MultiStepProgressBarProps<T>) => {
  const { activeStep, currentLabel, nextLabel, progress } = useActiveStep<T>({
    steps,
    currentStepId,
  });

  // Filter out unavailable steps for display but preserve indices
  const availableSteps = steps.filter((step) => step.available !== false);
  const currentStep = steps[activeStep];
  const availableStepIndex = availableSteps.findIndex(
    (step) => step.id === currentStep?.id
  );
  const displayActiveStep = availableStepIndex >= 0 ? availableStepIndex : 0;

  const resolvedColor = (color && UCLColors[color]) || UCLColors["forest-green"];

  // Custom theme for the stepper (CSS variable for color)
  const stepperStyle: React.CSSProperties = {
    "--ucl-stepper-active-color": resolvedColor,
    "--step-indicator-bg-color": resolvedColor,
    "--step-indicator-border-color": resolvedColor,
    "--step-indicator-active-bg-color": resolvedColor,
    "--step-indicator-active-border-color": resolvedColor,
    "--step-indicator-active-color": "#fff",
    ...style,
  } as React.CSSProperties;

  // Mobile circular progress bar annotation
  const annotationContent = `
    <div class='ucl-multistep-progress-annotation'>
      <span class='ucl-multistep-progress-annotation-value'>${Math.round(
        progress
      )}%</span>
    </div>
  `;

  return (
    <div className="ucl-multistep-progressbar-container">
      {/* Desktop stepper */}
      <div className={`ucl-multistep-stepper ${className || ""}`} style={stepperStyle}>
        <StepperComponent
          animation={{ enable: true, duration: 500, delay: 0 }}
          orientation="Horizontal"
          stepType="Default"
          labelPosition="Bottom"
          activeStep={displayActiveStep}
          cssClass="ucl-multistep-stepper"
          stepChanged={(e) => {
            if (onStepClick) {
              const idx = e.activeStep;
              const step = availableSteps[idx];
              if (step) onStepClick(step.id);
            }
          }}
        >
          <StepsDirective>
            {availableSteps.map((step, idx) => (
              <StepDirective
                key={step.id}
                label={step.label}
                text={`${idx + 1}`}
                aria-label={step.label}
              />
            ))}
          </StepsDirective>
        </StepperComponent>
      </div>

      {/* Mobile circular progress */}
      <div className={`ucl-multistep-circular-progress-row ${className || ""}`}>
        <ProgressBarComponent
          id="ucl-circular-progress"
          type="Circular"
          height="100px"
          width="100px"
          value={progress}
          progressColor={resolvedColor}
          trackColor="#e0e0e0"
          progressThickness={8}
          trackThickness={8}
          cornerRadius="Round"
          animation={{ enable: true, duration: 500, delay: 0 }}
          showProgressValue={false}
        >
          <Inject services={[ProgressAnnotation]} />
          <ProgressBarAnnotationsDirective>
            <ProgressBarAnnotationDirective content={annotationContent} />
          </ProgressBarAnnotationsDirective>
        </ProgressBarComponent>

        <div className="ucl-multistep-labels">
          <div className="ucl-multistep-labels__current">{currentLabel}</div>
          {nextLabel && (
            <div className="ucl-multistep-labels__next">Next: {nextLabel}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultiStepProgressBar;
